import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useLocalStorage from '@/hooks/useLocalStorage';
import {
  defaultProducts,
  defaultCategories,
  defaultCollections,
  type AdminProduct,
  type AdminCategory,
  type AdminCollection,
} from '@/mocks/adminData';

const allSizes = ['RN', 'P', 'M', 'G', 'GG', '1', '2', '4', '6', '8', '10', '12'];
const allColors = ['Branco', 'Preto', 'Rosa Claro', 'Azul Bebê', 'Floral Rosa', 'Floral Lilás', 'Azul Marinho', 'Verde Militar', 'Cinza', 'Kit Rosa', 'Kit Neutro', 'Kit Colorido', 'Jeans Claro', 'Jeans Médio', 'Estampa Dinossauro', 'Estampa Unicórnio', 'Estampa Espaço', 'Vermelho', 'Amarelo', 'Verde'];

const emptyProduct: AdminProduct = {
  id: '',
  name: '',
  category: '',
  collection: '',
  shortDescription: '',
  fullDescription: '',
  price: 0,
  promotionalPrice: null,
  sizes: [],
  colors: [],
  stock: 0,
  active: true,
  featured: false,
  mainImage: '',
  gallery: [],
  sku: '',
  internalNotes: '',
};

function generateId(products: AdminProduct[]): string {
  const max = products.reduce((acc, p) => {
    const n = parseInt(p.id.replace('prod-', ''), 10);
    return n > acc ? n : acc;
  }, 0);
  return `prod-${max + 1}`;
}

function ProductFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [products, setProducts] = useLocalStorage<AdminProduct[]>('admin_products', defaultProducts);
  const [categories] = useLocalStorage<AdminCategory[]>('admin_categories', defaultCategories);
  const [collections] = useLocalStorage<AdminCollection[]>('admin_collections', defaultCollections);
  const [form, setForm] = useState<AdminProduct>(emptyProduct);
  const [savedFlash, setSavedFlash] = useState(false);

  const isEditing = Boolean(id);

  useEffect(() => {
    if (id) {
      const existing = products.find((p) => p.id === id);
      if (existing) {
        setForm({ ...existing });
      } else {
        navigate('/admin/produtos');
      }
    } else {
      setForm({ ...emptyProduct, id: generateId(products) });
    }
  }, [id, products, navigate]);

  const update = (partial: Partial<AdminProduct>) => setForm((prev) => ({ ...prev, ...partial }));

  const toggleSize = (s: string) => {
    const has = form.sizes.includes(s);
    update({ sizes: has ? form.sizes.filter((x) => x !== s) : [...form.sizes, s] });
  };

  const toggleColor = (c: string) => {
    const has = form.colors.includes(c);
    update({ colors: has ? form.colors.filter((x) => x !== c) : [...form.colors, c] });
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.category) return;
    if (isEditing) {
      setProducts(products.map((p) => (p.id === form.id ? form : p)));
    } else {
      setProducts([...products, form]);
    }
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {savedFlash && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium shadow-sm">
          <i className="ri-check-line mr-1.5" />
          Alterações salvas com sucesso.
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate('/admin/produtos')}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-500 transition-colors"
        >
          <i className="ri-arrow-left-line" />
        </button>
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
            {isEditing ? 'Editar produto' : 'Novo produto'}
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Informações básicas</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Nome do produto</label>
                <input type="text" value={form.name} onChange={(e) => update({ name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" placeholder="Ex: Body Algodão Bebê" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Descrição curta</label>
                <input type="text" value={form.shortDescription} onChange={(e) => update({ shortDescription: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" placeholder="Resumo para o card do catálogo" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Descrição completa</label>
                <textarea value={form.fullDescription} onChange={(e) => { if (e.target.value.length <= 500) update({ fullDescription: e.target.value }); }} rows={4} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none" placeholder="Descrição detalhada do produto" />
                <p className="text-[10px] text-foreground-400 mt-1">{form.fullDescription.length}/500 caracteres</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Preço (R$)</label>
                  <input type="number" step="0.01" min="0" value={form.price || ''} onChange={(e) => update({ price: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" placeholder="0,00" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Preço promocional (R$)</label>
                  <input type="number" step="0.01" min="0" value={form.promotionalPrice ?? ''} onChange={(e) => update({ promotionalPrice: e.target.value ? parseFloat(e.target.value) : null })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" placeholder="Opcional" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">SKU / Código interno</label>
                <input type="text" value={form.sku} onChange={(e) => update({ sku: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent font-mono" placeholder="MCV-XXX-000" />
              </div>
            </div>
          </div>

          {/* Sizes & Colors */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Tamanhos e cores</h2>
            <div className="mb-5">
              <label className="block text-xs font-medium text-foreground-700 mb-2">Tamanhos disponíveis</label>
              <div className="flex flex-wrap gap-2">
                {allSizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSize(s)}
                    className={`w-10 h-9 flex items-center justify-center rounded-lg text-xs font-medium border transition-colors ${
                      form.sizes.includes(s)
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-background-50 text-foreground-600 border-background-300 hover:border-primary-300'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-2">Cores disponíveis</label>
              <div className="flex flex-wrap gap-2">
                {allColors.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleColor(c)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
                      form.colors.includes(c)
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-background-50 text-foreground-600 border-background-300 hover:border-primary-300'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Imagens</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Imagem principal (URL)</label>
                <input type="text" value={form.mainImage} onChange={(e) => update({ mainImage: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" placeholder="https://..." />
                {form.mainImage && (
                  <img src={form.mainImage} alt="Preview" className="mt-2 w-24 h-32 rounded-lg object-cover border border-background-200" />
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Galeria de imagens (URLs, uma por linha)</label>
                <textarea
                  value={form.gallery.join('\n')}
                  onChange={(e) => update({ gallery: e.target.value.split('\n').filter((u) => u.trim()) })}
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none font-mono"
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Status</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground-700">Ativo</span>
                <button
                  type="button"
                  onClick={() => update({ active: !form.active })}
                  className={`relative w-10 h-5 rounded-full transition-colors ${form.active ? 'bg-primary-600' : 'bg-background-300'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.active ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground-700">Produto em destaque</span>
                <button
                  type="button"
                  onClick={() => update({ featured: !form.featured })}
                  className={`relative w-10 h-5 rounded-full transition-colors ${form.featured ? 'bg-amber-500' : 'bg-background-300'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.featured ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Category & Collection */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Classificação</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Categoria</label>
                <select value={form.category} onChange={(e) => update({ category: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400">
                  <option value="">Selecionar...</option>
                  {categories.filter((c) => c.active).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Coleção</label>
                <select value={form.collection} onChange={(e) => update({ collection: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400">
                  <option value="">Selecionar...</option>
                  {collections.filter((c) => c.active).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Stock */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Estoque</h2>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Quantidade em estoque</label>
              <input type="number" min="0" value={form.stock} onChange={(e) => update({ stock: parseInt(e.target.value, 10) || 0 })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Observações internas</h2>
            <textarea value={form.internalNotes} onChange={(e) => update({ internalNotes: e.target.value })} rows={3} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none" placeholder="Notas visíveis apenas no painel..." />
          </div>
        </div>
      </div>

      {/* Save Bar */}
      <div className="flex items-center justify-between mt-6 pt-5 border-t border-background-200">
        <button type="button" onClick={() => navigate('/admin/produtos')} className="px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap">
          Cancelar
        </button>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate('/admin/produtos')} className="px-4 py-2.5 rounded-full text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap">
            Descartar
          </button>
          <button type="button" onClick={handleSave} className="px-5 py-2.5 rounded-full text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors whitespace-nowrap">
            Salvar alterações
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductFormPage;